"use client";

import { useState, useEffect, useRef } from 'react';

interface TextScrambleProps {
  text: string;
  className?: string;
  speed?: number;
  iterations?: number;
}

export function TextScramble({ 
  text, 
  className = '',
  speed = 60,
  iterations = 10
}: TextScrambleProps) {
  const [displayText, setDisplayText] = useState(text);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const originalTextRef = useRef(text);
  const iterationRef = useRef(0);
  const chars = '!<>-_\\/[]{}—=+*^?#_$%&@';
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Update original text if prop changes
    originalTextRef.current = text;
    if (!isHovering) {
      setDisplayText(text);
    }
  }, [text, isHovering]);

  const scrambleText = () => {
    if (iterationRef.current >= iterations) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setDisplayText(originalTextRef.current);
      iterationRef.current = 0;
      return;
    }

    let newText = '';
    const original = originalTextRef.current;
    const progress = iterationRef.current / iterations;

    for (let i = 0; i < original.length; i++) {
      // If this character should be scrambled or not
      if (i % 2 === iterationRef.current % 2) {
        // Alternate scrambling pattern for a more interesting effect
        if (Math.random() < progress || original[i] === ' ' || original[i] === '[' || original[i] === ']') {
          newText += original[i];
        } else {
          newText += chars[Math.floor(Math.random() * chars.length)];
        }
      } else if (Math.random() < 0.7 || original[i] === ' ' || original[i] === '[' || original[i] === ']') {
        // Non-alternating characters have a higher chance to show original
        newText += original[i];
      } else {
        newText += chars[Math.floor(Math.random() * chars.length)];
      }
    }

    setDisplayText(newText);
    iterationRef.current++;
  };

  const startScramble = () => {
    if (isHovering) return; // Prevent multiple starts
    
    setIsHovering(true);
    
    // Clear any existing timeout to prevent multiple intervals
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Small delay before starting animation
    hoverTimeoutRef.current = setTimeout(() => {
      iterationRef.current = 0;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(scrambleText, speed);
    }, 50);
  };

  const stopScramble = () => {
    if (!isHovering) return; // Prevent multiple stops
    
    setIsHovering(false);
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Add final scramble effect when leaving
    let finalIterations = 0;
    const maxFinalIterations = 5;
    
    const finalInterval = setInterval(() => {
      if (finalIterations >= maxFinalIterations) {
        clearInterval(finalInterval);
        setDisplayText(originalTextRef.current);
        iterationRef.current = 0;
        return;
      }
      
      let newText = '';
      const original = originalTextRef.current;
      
      for (let i = 0; i < original.length; i++) {
        if (Math.random() < (finalIterations / maxFinalIterations) || original[i] === ' ' || original[i] === '[' || original[i] === ']') {
          newText += original[i];
        } else {
          newText += chars[Math.floor(Math.random() * chars.length)];
        }
      }
      
      setDisplayText(newText);
      finalIterations++;
    }, speed / 2);
    
    // Cleanup final interval after a set time
    setTimeout(() => {
      clearInterval(finalInterval);
      setDisplayText(originalTextRef.current);
    }, 300);
  };

  // React event handlers always observe the current props and hover state.
  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  return (
    <span 
      ref={spanRef}
      onMouseEnter={startScramble}
      onMouseOver={startScramble}
      onMouseLeave={stopScramble}
      className={`hidden w-1 h-1 lg:w-auto lg:h-auto lg:inline-block hover:cursor-pointer ${className}`}
      style={{ pointerEvents: 'auto' }}
    >
      {displayText}
    </span>
  );
}
